import type {
  ChipId,
  FormPoint,
  Game,
  GameweekDetail,
  GameweekResult,
  Prediction,
  SeasonStats,
} from './types'
import {
  AUTO_PICK_POINTS,
  applyMultipliers,
  isCorrectOutcome,
  scorePrediction,
} from './scoring'

export function computeGameweekDetail(
  gameweek: number,
  games: Game[],
  predictions: Record<string, Prediction>,
  results: Record<string, { homeScore: number; awayScore: number }>,
  bankerGameId: string | null,
  isDoubleUpActive: boolean,
  isCleanSweepActive: boolean,
  isGoalfestActive: boolean
): GameweekDetail {
  // Which chips this total was scored with, recorded on the detail so any screen showing it can
  // name them without consulting a ledger that may belong to someone else.
  const activeChips: ChipId[] = [
    isDoubleUpActive && 'doubleUp',
    isCleanSweepActive && 'cleanSweep',
    isGoalfestActive && 'goalfest',
  ].filter((id): id is ChipId => id !== false)

  // Clean Sweep fully replaces the normal 5/3/1 scoring for the gameweek — it's
  // mutually exclusive with every other chip, so there's no
  // Double Up/Goalfest scoring to also apply here.
  if (isCleanSweepActive) {
    const totalPoints = computeCleanSweepPoints(games, predictions, results, bankerGameId)
    return { gameweek, totalPoints, games, predictions, results, activeChips }
  }

  let totalPoints = 0

  for (const game of games) {
    const actual = results[game.id]
    const prediction = predictions[game.id]
    if (!actual || !prediction) continue

    const basePoints = scorePrediction(prediction, actual).points

    totalPoints += applyMultipliers(basePoints, game.id === bankerGameId, isDoubleUpActive)

    if (
      isGoalfestActive &&
      !prediction.isAutoPredicted &&
      isCorrectOutcome(
        prediction.homeScore,
        prediction.awayScore,
        actual.homeScore,
        actual.awayScore
      )
    ) {
      totalPoints += actual.homeScore + actual.awayScore
    }
  }

  return { gameweek, totalPoints, games, predictions, results, activeChips }
}

/** Points for a single Clean Sweep fixture once the gameweek sweep has cleared: 20 for an
 * exact score, 10 otherwise, doubled if it's the bankered fixture.
 *
 * An auto pick still counts toward clearing (or breaking) the sweep — it is the player's pick
 * and the sweep is all-or-nothing — but pays its capped 2 rather than the sweep rate, so the
 * chip can't launder it past the cap. A cleared sweep means its outcome was right, hence the
 * flat 2. */
export function cleanSweepGameValue(
  isExact: boolean,
  isBankered: boolean,
  isAutoPredicted = false
): number {
  const base = isAutoPredicted ? AUTO_PICK_POINTS : isExact ? 20 : 10
  return isBankered ? base * 2 : base
}

/**
 * Whether every fixture with both a prediction and a result has its outcome (win/draw/loss)
 * called correctly — an exact score counts too, via `isCorrectOutcome`. Fixtures with no
 * prediction/result (postponed, not yet played) are skipped, matching the rest of this
 * file's skip-if-missing convention; they neither clear nor break the sweep.
 */
export function isCleanSweepCleared(
  games: Game[],
  predictions: Record<string, Prediction>,
  results: Record<string, { homeScore: number; awayScore: number }>
): boolean {
  for (const game of games) {
    const actual = results[game.id]
    const prediction = predictions[game.id]
    if (!actual || !prediction) continue

    if (
      !isCorrectOutcome(
        prediction.homeScore,
        prediction.awayScore,
        actual.homeScore,
        actual.awayScore
      )
    ) {
      return false
    }
  }
  return true
}

/**
 * Clean Sweep's gameweek total: 0 if any played fixture's outcome was missed, otherwise the
 * sum of 20 (exact) / 10 (correct outcome) per fixture, doubled for the bankered fixture.
 * Also doubles as the live in-progress total when called with a partial
 * `results` view — fixtures without a result yet are simply skipped, the same way
 * `computeGoalfestBonus` reads live results.
 */
export function computeCleanSweepPoints(
  games: Game[],
  predictions: Record<string, Prediction>,
  results: Record<string, { homeScore: number; awayScore: number }>,
  bankerGameId: string | null
): number {
  if (!isCleanSweepCleared(games, predictions, results)) return 0

  let total = 0
  for (const game of games) {
    const actual = results[game.id]
    const prediction = predictions[game.id]
    if (!actual || !prediction) continue

    const isExact =
      actual.homeScore === prediction.homeScore && actual.awayScore === prediction.awayScore
    total += cleanSweepGameValue(isExact, game.id === bankerGameId, prediction.isAutoPredicted)
  }
  return total
}

/**
 * The live Goalfest bonus so far: the sum of
 * `homeScore + awayScore` across every fixture that's currently a
 * correct-outcome call, using whatever (possibly partial/live) results are
 * available. Recomputes as scores change and can decrease if an outcome
 * flips from correct to incorrect.
 *
 * Auto picks are skipped: a pick nobody made pays its capped 2 and nothing more.
 */
export function computeGoalfestBonus(
  games: Game[],
  predictions: Record<string, Prediction>,
  results: Record<string, { homeScore: number; awayScore: number }>
): number {
  let bonus = 0
  for (const game of games) {
    const actual = results[game.id]
    const prediction = predictions[game.id]
    if (!actual || !prediction || prediction.isAutoPredicted) continue

    if (
      isCorrectOutcome(
        prediction.homeScore,
        prediction.awayScore,
        actual.homeScore,
        actual.awayScore
      )
    ) {
      bonus += actual.homeScore + actual.awayScore
    }
  }
  return bonus
}

/**
 * The gameweek scored on the football alone: plain 5/3/1 per fixture, with the
 * Banker's doubling applied and Double Up / Goalfest / Clean Sweep all ignored.
 *
 * The Banker is in because it is a weekly pick every player makes every week, so it doesn't
 * distort a comparison between weeks. The chips are out because including them would make Best
 * Gameweek win by construction on whichever week Double Up was spent — the two numbers have to be
 * free to disagree, since one measures how well you read the football and the other when you
 * chose to spend.
 */
export function computeBasePoints(
  games: Game[],
  predictions: Record<string, Prediction>,
  results: Record<string, { homeScore: number; awayScore: number }>,
  bankerGameId: string | null
): number {
  let total = 0
  for (const game of games) {
    const actual = results[game.id]
    const prediction = predictions[game.id]
    if (!actual || !prediction) continue

    const points = scorePrediction(prediction, actual).points
    total += applyMultipliers(points, game.id === bankerGameId, false)
  }
  return total
}

export function computeGameweekResult(
  detail: GameweekDetail,
  bankerGameId: string | null
): GameweekResult {
  let exactScores = 0
  let correctOutcomes = 0
  let partialScores = 0
  let totalGames = 0
  let ratedGames = 0
  let ratedOutcomes = 0

  for (const game of detail.games) {
    const actual = detail.results[game.id]
    const prediction = detail.predictions[game.id]
    if (!actual || !prediction) continue

    // Counted here, past the same guard the outcome counters sit behind, so numerator and
    // denominator always describe the same set of fixtures. Taking the round's whole fixture
    // list instead put Saturday's unplayed matches in the denominator from the first whistle
    // to the last, and every profile read as a worse predictor than it was all weekend.
    totalGames++

    const isExact =
      actual.homeScore === prediction.homeScore && actual.awayScore === prediction.awayScore
    const calledOutcome = isCorrectOutcome(
      prediction.homeScore,
      prediction.awayScore,
      actual.homeScore,
      actual.awayScore
    )

    // A 1–1 auto pick that happens to land is not an exact score — it pays 2, and counting it
    // here would put it in the profile's season stats and the best-gameweek tiebreak on the
    // strength of a pick nobody made.
    if (!prediction.isAutoPredicted && isExact) exactScores++

    if (calledOutcome) {
      correctOutcomes++
    } else if (scorePrediction(prediction, actual).points === 1) {
      partialScores++
    }

    // The Intuition Rating's pool: fixtures the player actually picked, auto fills left out of
    // both halves together. Dropping them from the numerator alone would score someone 0 for
    // calls nobody made; dropping them from both means a week you forgot moves the rating by
    // nothing at all, which is the honest answer for a season-long read on your judgement.
    if (!prediction.isAutoPredicted) {
      ratedGames++
      if (calledOutcome) ratedOutcomes++
    }
  }

  let bankerHit: boolean | null = null
  if (bankerGameId) {
    const actual = detail.results[bankerGameId]
    const prediction = detail.predictions[bankerGameId]
    if (actual && prediction) {
      bankerHit =
        actual.homeScore === prediction.homeScore &&
        actual.awayScore === prediction.awayScore
    }
  }

  return {
    gameweek: detail.gameweek,
    points: detail.totalPoints,
    basePoints: computeBasePoints(
      detail.games,
      detail.predictions,
      detail.results,
      bankerGameId
    ),
    exactScores,
    correctOutcomes,
    partialScores,
    totalGames,
    ratedOutcomes,
    ratedGames,
    bankerGameId: bankerGameId ?? null,
    bankerHit,
  }
}
/**
 * Every finalized gameweek number, deduplicated and sorted ascending.
 */
export function getSortedFinalizedGameweeks(history: GameweekResult[]): number[] {
  return [...new Set(history.map((gw) => gw.gameweek))].sort((a, b) => a - b)
}

/**
 * Most recently finalized gameweek, or null if none exist yet.
 */
export function getMostRecentFinalizedGameweek(history: GameweekResult[]): number | null {
  const gameweeks = getSortedFinalizedGameweeks(history)
  return gameweeks.length === 0 ? null : gameweeks[gameweeks.length - 1]
}

/**
 * Steps to the previous/next finalized gameweek relative to `currentGameweek`.
 * Returns null at the earliest/latest boundary (or if `currentGameweek` isn't finalized).
 */
export function getAdjacentFinalizedGameweek(
  history: GameweekResult[],
  currentGameweek: number,
  direction: 'previous' | 'next'
): number | null {
  const gameweeks = getSortedFinalizedGameweeks(history)
  const index = gameweeks.indexOf(currentGameweek)
  if (index === -1) return null

  const targetIndex = direction === 'previous' ? index - 1 : index + 1
  return gameweeks[targetIndex] ?? null
}

/**
 * The neighbour of `current` in an explicit, ascending list — what the results modal's arrows
 * step by when they were opened from a set of their own rather than from the season (ticket
 * A multi-charge chip's uses). Null at either end, so the arrows disable rather than wrap.
 */
export function adjacentIn(
  gameweeks: number[],
  current: number,
  direction: 'previous' | 'next'
): number | null {
  const index = gameweeks.indexOf(current)
  if (index === -1) return null
  return gameweeks[direction === 'previous' ? index - 1 : index + 1] ?? null
}

/**
 * Aggregates season-long headline stats across all finalized gameweeks.
 *
 * Both rates run off the **rated** counters — the fixtures the player picked themselves — never
 * off `correctOutcomes`/`totalGames`, which describe everything that happened in the week,
 * auto fills included. The two are separate fields precisely so this cannot drift: the Intuition
 * Rating is a season-long claim about someone's judgement, and Sharpest Week is a description of
 * one gameweek. `exactScores` already excluded auto picks, so it was reading against a
 * denominator that did not.
 */
export function computeSeasonStats(history: GameweekResult[]): SeasonStats {
  let correctPredictions = 0
  let correctOutcomeCount = 0
  let totalGamesPredicted = 0
  let bestGameweek: SeasonStats['bestGameweek'] = null

  for (const gw of history) {
    correctPredictions += gw.exactScores
    correctOutcomeCount += gw.ratedOutcomes
    totalGamesPredicted += gw.ratedGames

    if (bestGameweek === null || isBetterGameweek(gw, bestGameweek)) {
      bestGameweek = {
        gameweek: gw.gameweek,
        basePoints: gw.basePoints,
        points: gw.points,
        correctOutcomes: gw.correctOutcomes,
        partialScores: gw.partialScores,
        exactScores: gw.exactScores,
      }
    }
  }

  return {
    correctPredictions,
    totalGamesPredicted,
    accuracyPct:
      totalGamesPredicted === 0 ? null : (correctPredictions / totalGamesPredicted) * 100,
    correctOutcomeCount,
    correctOutcomePct:
      totalGamesPredicted === 0 ? null : (correctOutcomeCount / totalGamesPredicted) * 100,
    bestGameweek,
  }
}

/** Best gameweek is decided on base points, then on the higher exact count, then on the earlier
 *  gameweek — so a season's best week is a stable answer rather than whichever row came last. */
function isBetterGameweek(
  candidate: GameweekResult,
  incumbent: NonNullable<SeasonStats['bestGameweek']>
): boolean {
  if (candidate.basePoints !== incumbent.basePoints) {
    return candidate.basePoints > incumbent.basePoints
  }
  if (candidate.exactScores !== incumbent.exactScores) {
    return candidate.exactScores > incumbent.exactScores
  }
  return candidate.gameweek < incumbent.gameweek
}

/**
 * Baseline correct-outcome rate for "always predict the home team to win",
 * applied to every actual fixture result for the season/league — the
 * reference point the skill meter's "Home picks" pin marks against a real
 * player's correct-outcome %. This is independent of any single player's
 * predictions (unlike `GameweekResult.correctOutcomes`, which is relative to
 * what was actually predicted), so it takes actual results directly rather
 * than a player's `GameweekResult[]` history.
 */
export function computeHomePickBaselinePct(
  actualResultsByGameweek: Record<
    number,
    Record<string, { homeScore: number; awayScore: number }>
  >
): number {
  let homeWins = 0
  let total = 0

  for (const gwResults of Object.values(actualResultsByGameweek)) {
    for (const result of Object.values(gwResults)) {
      total++
      if (result.homeScore > result.awayScore) homeWins++
    }
  }

  return total === 0 ? 0 : (homeWins / total) * 100
}

/**
 * Per-gameweek points across the season, sorted ascending by gameweek — the
 * "form" (this week's result), not a cumulative running total, so a slump or
 * hot streak is still visible.
 */
export function computeFormHistory(history: GameweekResult[]): FormPoint[] {
  return [...history]
    .sort((a, b) => a.gameweek - b.gameweek)
    .map((gw) => ({ gameweek: gw.gameweek, points: gw.points }))
}